import { useEffect, useState } from "react";
import { FaRegStar } from "react-icons/fa6";
import { IoSearchOutline } from "react-icons/io5";
import LoadingSpinner from "../../../../components/elements/LoadingSpinner";
import Pagination from "../../../../components/Pagination";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { RootState } from "../../../../redux/store";
import {
  getAllRestaurants,
  getSuspendedRestaurants,
  RestaurantListItem,
  SuspendedRestaurantItem,
} from "../../../../redux/restaurants/restaurantsSlice";
import {
  getAllAppeals,
  Appeal,
} from "../../../../redux/restaurantAppeals/restaurantAppealsSlice";
import {
  pendingRestaurantsData,
} from "../../../../data/restaurantStatusData";
import PendingRestaurantModal from "../../../../components/modals/Restaurant/PendingRestaurantModal";
import SuspendedRestaurantModal from "../../../../components/modals/Restaurant/SuspendedRestaurantModal";
import AppealRestaurantModal from "../../../../components/modals/Restaurant/AppealRestaurantModal";

type TabType = "active" | "suspended" | "appeal";

interface VendorTableProps {
  activeTab?: TabType;
}

const VendorTable = ({ activeTab = "active" }: VendorTableProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { restaurants, suspendedRestaurants, status, suspendedStatus, error } =
    useAppSelector((state: RootState) => state.restaurants);
  const { appeals, status: appealsStatus } = useAppSelector(
    (state: RootState) => state.restaurantAppeals
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<
    string | null
  >(null);
  const [selectedTabType, setSelectedTabType] = useState<string | null>(null);
  const itemsPerPage = 10;

  useEffect(() => {
    // Fetch real restaurants for active tab
    if (activeTab === "active") {
      dispatch(getAllRestaurants());
    }
    // Fetch suspended restaurants for suspended tab
    if (activeTab === "suspended") {
      dispatch(getSuspendedRestaurants());
    }
    // Fetch appeals for appeal tab
    if (activeTab === "appeal") {
      dispatch(getAllAppeals());
    }
  }, [dispatch, activeTab]);

  // Get data based on active tab
  const getTabData = () => {
    switch (activeTab) {
      case "active":
        return restaurants.filter(
          (restaurant) =>
            restaurant.status === "active" &&
            restaurant.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      // case "pending":
      //   return pendingRestaurantsData.filter((restaurant) =>
      //     restaurant.name.toLowerCase().includes(searchTerm.toLowerCase())
      //   );
      case "suspended":
        return suspendedRestaurants.filter((restaurant) =>
          restaurant.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      case "appeal":
        return appeals.filter((appeal) => {
          const restaurantName = appeal.restaurantId?.businessInformation?.businessName || "";
          const appealId = appeal.appealId || "";
          const q = searchTerm.toLowerCase();
          return restaurantName.toLowerCase().includes(q) || appealId.toLowerCase().includes(q);
        });
      default:
        return [];
    }
  };

  const filteredRestaurants = getTabData();

  // Pagination logic
  const totalItems = filteredRestaurants.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRestaurants = filteredRestaurants.slice(startIndex, endIndex);

  const handleViewDetails = (restaurantId: string, tabType: string) => {
    setSelectedRestaurantId(restaurantId);
    setSelectedTabType(tabType);
  };

  const handleCloseModal = () => {
    setSelectedRestaurantId(null);
    setSelectedTabType(null);
  };

  const handlePagination = (page: number) => {
    setCurrentPage(page);
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Reset to first page when search or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  const isLoading =
    (activeTab === "active" && status === "loading") ||
    (activeTab === "suspended" && suspendedStatus === "loading") ||
    (activeTab === "appeal" && appealsStatus === "loading");

  const getColSpan = (): number => {
    switch (activeTab) {
      case "suspended":
      case "appeal":
        return 5;
      // case "pending":
      //   return 4;
      default:
        return 8;
    }
  };

  const getTableTitle = () => {
    switch (activeTab) {
      case "active":
        return "Vendor Directory";
      // case "pending":
      //   return "Restaurants Pending Approval";
      case "suspended":
        return "Suspended Restaurants";
      case "appeal":
        return "Restaurant Appeals";
      default:
        return "Vendor Directory";
    }
  };

  const renderTableHeaders = () => {
    if (activeTab === "suspended") {
      return (
        <tr className="bg-white">
          <th className="p-3 font-medium">Restaurant ID</th>
          <th className="p-3 font-medium">Suspension Reason</th>
          <th className="p-3 font-medium">Suspension Date</th>
          <th className="p-3 font-medium">Wallet Balance</th>
          <th className="p-3 font-medium">Actions</th>
        </tr>
      );
    } else if (activeTab === "appeal") {
      return (
        <tr className="bg-white">
          <th className="p-3 font-medium">Restaurant</th>
          <th className="p-3 font-medium">Appeal Reason</th>
          <th className="p-3 font-medium">Submitted Date</th>
          <th className="p-3 font-medium">Status</th>
          <th className="p-3 font-medium">Actions</th>
        </tr>
      );
    }
    // else if (activeTab === "pending") {
    //   return (
    //     <tr className="bg-white">
    //       <th className="p-3 font-medium">Restaurant Name</th>
    //       <th className="p-3 font-medium">Application Date</th>
    //       <th className="p-3 font-medium">Status</th>
    //       <th className="p-3 font-medium">Actions</th>
    //     </tr>
    //   );
    // }
    else {
      return (
        <tr className="bg-white">
          <th className="p-3 font-medium">Restaurant ID</th>
          <th className="p-3 font-medium">Restaurant Name</th>
          <th className="p-3 font-medium">Location</th>
          <th className="p-3 font-medium">Rating</th>
          <th className="p-3 font-medium">Orders</th>
          <th className="p-3 font-medium">Revenue</th>
          <th className="p-3 font-medium">Status</th>
          <th className="p-3 font-medium">Actions</th>
        </tr>
      );
    }
  };

  const renderTableRows = () => {
    if (activeTab === "suspended") {
      return (paginatedRestaurants as SuspendedRestaurantItem[]).map(
        (restaurant) => (
          <tr key={restaurant.id} className="border text-sm hover:bg-gray-100">
            <td className="p-5">{restaurant.id}</td>
            <td className="p-5">{restaurant.suspensionReason}</td>
            <td className="p-5">{restaurant.suspensionDate}</td>
            <td className="p-5">
              ₦{restaurant.walletBalance.toLocaleString()}
            </td>
            <td className="p-5">
              <button
                className="text-secondary underline"
                onClick={() => handleViewDetails(restaurant.id, "suspended")}
              >
                Review
              </button>
            </td>
          </tr>
        )
      );
    } else if (activeTab === "appeal") {
      return (paginatedRestaurants as Appeal[]).map((appeal) => (
        <tr key={appeal._id} className="border text-sm hover:bg-gray-100">
          <td className="p-5">
            {appeal.restaurantId?.businessInformation?.businessName || "N/A"}
          </td>
          <td className="p-5">{appeal.reason}</td>
          <td className="p-5">
            {new Date(appeal.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </td>
          <td className="p-5">
            <span
              className={`text-xs px-2 py-1 rounded ${
                appeal.status === "open"
                  ? "text-orange-500 bg-orange-100"
                  : "text-green-500 bg-green-100"
              }`}
            >
              {appeal.status === "open" ? "Under Review" : "Closed"}
            </span>
          </td>
          <td className="p-5">
            <button
              className="text-secondary underline"
              onClick={() => handleViewDetails(appeal._id, "appeal")}
            >
              Review Appeal
            </button>
          </td>
        </tr>
      ));
    }
    // else if (activeTab === "pending") {
    //   return (paginatedRestaurants as PendingRestaurant[]).map((restaurant) => (
    //     <tr key={restaurant.id} className="border text-sm hover:bg-gray-100">
    //       <td className="p-5 flex items-center gap-2">
    //         <img
    //           src={restaurant.image}
    //           alt={restaurant.name}
    //           className="w-12 h-12 rounded-full object-cover"
    //         />
    //         {restaurant.name}
    //       </td>
    //       <td className="p-5">{restaurant.applicationDate}</td>
    //       <td className="p-5">
    //         <span
    //           className={`text-xs px-2 py-1 rounded ${
    //             restaurant.status === "pending"
    //               ? "text-orange-500 bg-orange-100"
    //               : "text-red-500 bg-red-100"
    //           }`}
    //         >
    //           {restaurant.status === "pending"
    //             ? "Pending"
    //             : "Missing Documents"}
    //         </span>
    //       </td>
    //       <td className="p-5">
    //         <button
    //           className="text-secondary underline"
    //           onClick={() => handleViewDetails(restaurant.id, "pending")}
    //         >
    //           View Details
    //         </button>
    //       </td>
    //     </tr>
    //   ));
    // }
    else {
      // Default active restaurants view
      return (paginatedRestaurants as RestaurantListItem[]).map(
        (restaurant) => (
          <tr key={restaurant.id} className="border text-sm hover:bg-gray-100">
            <td className="p-5">{restaurant.id}</td>
            <td className="p-5">{restaurant.name}</td>
            <td className="p-5">{restaurant.location || "N/A"}</td>
            <td className="p-5">
              <div className="flex items-center gap-1">
                {restaurant.rating}
                <FaRegStar className="text-orange100" />
              </div>
            </td>
            <td className="p-5">{restaurant.orders}</td>
            <td className="p-5">₦{restaurant.revenue.toLocaleString()}</td>
            <td className="p-5">
              <p
                className={`${
                  restaurant.status === "active"
                    ? "bg-[#008B501F] text-secondary"
                    : restaurant.status === "suspended"
                    ? "text-red-500 bg-[#EA19191F]"
                    : restaurant.status === "inactive"
                    ? "text-gray-500 bg-gray-100"
                    : ""
                } text-center rounded-md p-1 text-[12px] capitalize`}
              >
                {restaurant.status}
              </p>
            </td>
            <td className="p-5">
              <button
                className="text-secondary underline"
                onClick={() =>
                  navigate(
                    `/dashboard/restaurants/operations/vendor/${restaurant.id}`
                  )
                }
              >
                View details
              </button>
            </td>
          </tr>
        )
      );
    }
  };

  return (
    <section className="font-mont">
      <div className="flex items-center gap-10 p-2 mb-4">
        <h3 className="font-medium text-lg">{getTableTitle()}</h3>
        <div className="relative md:w-[30rem] w-fit mb-3">
          <IoSearchOutline className="w-5 h-5 absolute top-[0.6rem] left-2 text-gray-300" />
          <input
            type="search"
            name="search"
            id="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border p-2 text-sm rounded-md indent-7 w-full"
            placeholder="Search Vendors"
          />
        </div>
      </div>
      <div className="min-h-[400px]">
        <table className="w-full">
          <thead className="font-normal">{renderTableHeaders()}</thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={getColSpan()} className="text-center py-4">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : error &&
              (activeTab === "active" || activeTab === "suspended") ? (
              <tr>
                <td
                  colSpan={getColSpan()}
                  className="text-center py-4 text-red-500"
                >
                  Error loading {activeTab} restaurants: {error}
                </td>
              </tr>
            ) : paginatedRestaurants && paginatedRestaurants.length > 0 ? (
              renderTableRows()
            ) : (
              <tr>
                <td colSpan={getColSpan()} className="text-center py-4">
                  {searchTerm
                    ? "No restaurants match your search."
                    : activeTab === "appeal"
                    ? "No restaurant appeals at the moment."
                    : // : // : activeTab === "pending"
                    // // ? "No restaurants pending approval."
                    activeTab === "suspended"
                    ? "No suspended restaurants."
                    : "No restaurants found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredRestaurants.length > 0 && (
        <div className="mt-6">
          <Pagination
            itemsPerPage={itemsPerPage}
            length={totalItems}
            currentPage={currentPage}
            handlePagination={handlePagination}
            prevPage={prevPage}
            nextPage={nextPage}
          />
        </div>
      )}

      {/* Modals */}
      {/* {selectedRestaurantId && selectedTabType === "pending" && (
        <PendingRestaurantModal
          toggleModal={handleCloseModal}
          restaurantId={selectedRestaurantId}
        />
      )} */}

      {selectedRestaurantId && selectedTabType === "suspended" && (
        <SuspendedRestaurantModal
          toggleModal={handleCloseModal}
          restaurantId={selectedRestaurantId}
        />
      )}

      {selectedRestaurantId && selectedTabType === "appeal" && (
        <AppealRestaurantModal
          toggleModal={handleCloseModal}
          restaurantId={selectedRestaurantId}
        />
      )}
    </section>
  );
};

export default VendorTable;
